(() => {
  const HOLD_DEFAULT = 5000;
  const TRANSITION_DEFAULT = 600;

  class ChameleonMarketCycle {
    constructor(root) {
      this.root = root;
      this.scenes = [...root.querySelectorAll('[data-cha-market-scene]')];
      this.label = root.querySelector('[data-cha-market-label]');
      this.poster = root.querySelector('.cha-commerce__market-poster');
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.hold = Number(root.dataset.hold) || HOLD_DEFAULT;
      this.transition = Number(root.dataset.transition) || TRANSITION_DEFAULT;
      this.index = 0;
      this.remaining = this.hold;
      this.holdStarted = 0;
      this.holdTimer = null;
      this.preloadTimer = null;
      this.animations = [];
      this.pausedByPointer = false;
      this.pausedByFocus = false;
      this.pausedByVisibility = document.hidden;
      this.bind();
      this.resetForMotionPreference();
    }

    bind() {
      this.root.addEventListener('pointerenter', () => { this.pausedByPointer = true; this.pauseCycle(); });
      this.root.addEventListener('pointerleave', () => { this.pausedByPointer = false; this.resume(); });
      this.root.addEventListener('focusin', () => { this.pausedByFocus = true; this.pauseCycle(); });
      this.root.addEventListener('focusout', () => {
        window.setTimeout(() => {
          this.pausedByFocus = this.root.contains(document.activeElement);
          this.resume();
        }, 0);
      });
      document.addEventListener('visibilitychange', () => {
        this.pausedByVisibility = document.hidden;
        if (document.hidden) this.pauseAll(); else this.resume();
      });
      this.motionQuery.addEventListener?.('change', () => this.resetForMotionPreference());
    }

    isPaused() { return this.pausedByPointer || this.pausedByFocus || this.pausedByVisibility; }

    ensureLoaded(scene) {
      const video = scene?.querySelector('video');
      if (!video || video.src) return video;
      video.src = video.dataset.src;
      video.load();
      return video;
    }

    play(scene, restart = false) {
      const video = this.ensureLoaded(scene);
      if (!video || this.pausedByVisibility) return;
      if (restart) {
        try { video.currentTime = 0; } catch (_) {}
      }
      video.play().catch(() => {});
    }

    pauseVideos() { this.scenes.forEach((scene) => scene.querySelector('video')?.pause()); }

    setCurrent(index) {
      this.scenes.forEach((scene, sceneIndex) => {
        const current = sceneIndex === index;
        scene.classList.toggle('is-current', current);
        scene.classList.remove('is-entering');
        scene.style.transform = current ? 'translate3d(0,0,0)' : 'translate3d(100%,0,0)';
        scene.setAttribute('aria-hidden', current ? 'false' : 'true');
        scene.tabIndex = current ? 0 : -1;
      });
      this.index = index;
      this.label.textContent = this.scenes[index].dataset.label;
    }

    resetForMotionPreference() {
      window.clearTimeout(this.holdTimer);
      window.clearTimeout(this.preloadTimer);
      this.animations.forEach((animation) => animation.cancel());
      this.animations = [];
      this.pauseVideos();
      this.setCurrent(0);
      this.remaining = this.hold;
      this.poster.hidden = false;
      this.root.classList.toggle('is-reduced-motion', this.motionQuery.matches);
      this.play(this.scenes[0]);
      if (!this.isPaused()) this.startHold(this.hold);
    }

    startHold(duration) {
      window.clearTimeout(this.holdTimer);
      window.clearTimeout(this.preloadTimer);
      this.remaining = duration;
      this.holdStarted = performance.now();
      const next = (this.index + 1) % this.scenes.length;
      this.preloadTimer = window.setTimeout(() => this.ensureLoaded(this.scenes[next]), Math.max(0, duration - 1500));
      this.holdTimer = window.setTimeout(() => this.beginTransition(), duration);
    }

    beginTransition() {
      if (this.isPaused() || this.animations.length) return;
      window.clearTimeout(this.preloadTimer);
      const current = this.scenes[this.index];
      const nextIndex = (this.index + 1) % this.scenes.length;
      const next = this.scenes[nextIndex];
      this.play(next, true);
      if (this.motionQuery.matches) {
        current.querySelector('video')?.pause();
        this.setCurrent(nextIndex);
        this.poster.hidden = true;
        this.remaining = this.hold;
        if (!this.isPaused()) this.startHold(this.hold);
        return;
      }
      next.classList.add('is-entering');
      next.setAttribute('aria-hidden', 'false');
      const outgoing = current.animate(
        [{ transform: 'translate3d(0,0,0)' }, { transform: 'translate3d(-100%,0,0)' }],
        { duration: this.transition, easing: 'ease-in-out', fill: 'forwards' }
      );
      const incoming = next.animate(
        [{ transform: 'translate3d(100%,0,0)' }, { transform: 'translate3d(0,0,0)' }],
        { duration: this.transition, easing: 'ease-in-out', fill: 'forwards' }
      );
      this.animations = [outgoing, incoming];
      incoming.onfinish = () => {
        const finishedAnimations = this.animations;
        this.animations = [];
        finishedAnimations.forEach((animation) => animation.cancel());
        current.querySelector('video')?.pause();
        this.setCurrent(nextIndex);
        this.poster.hidden = true;
        this.remaining = this.hold;
        if (!this.isPaused()) this.startHold(this.hold);
      };
    }

    pauseCycle() {
      this.animations.forEach((animation) => animation.pause());
      if (this.holdTimer) {
        window.clearTimeout(this.holdTimer);
        window.clearTimeout(this.preloadTimer);
        this.holdTimer = null;
        this.preloadTimer = null;
        this.remaining = Math.max(0, this.remaining - (performance.now() - this.holdStarted));
      }
    }

    pauseAll() {
      this.pauseCycle();
      this.pauseVideos();
    }

    resume() {
      if (this.isPaused()) return;
      this.play(this.scenes[this.index]);
      if (this.animations.length) {
        this.play(this.scenes[(this.index + 1) % this.scenes.length]);
        this.animations.forEach((animation) => animation.play());
      } else if (!this.holdTimer) {
        if (this.remaining <= 0) this.beginTransition(); else this.startHold(this.remaining);
      }
    }
  }

  const initialize = () => {
    document.querySelectorAll('[data-cha-market-cycle]').forEach((root) => {
      if (root.dataset.chaMarketCycleReady) return;
      root.dataset.chaMarketCycleReady = 'true';
      new ChameleonMarketCycle(root);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize);
  else initialize();
  document.addEventListener('shopify:section:load', initialize);
})();
